import type { ReactNode } from 'react';
import { cn } from '~/lib/cn';
import { LockIcon, UploadIcon } from './icons';

/** An empty surface is an invitation to act: what to do, and the buttons to do it. */
export function EmptyState({
  title,
  children,
  actions,
  icon = <UploadIcon size={26} weight={1.8} />,
  className,
}: {
  title: string;
  children?: ReactNode;
  actions?: ReactNode;
  icon?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3.5 px-6 py-7 text-center',
        className,
      )}
    >
      <span
        aria-hidden="true"
        className="flex size-[52px] items-center justify-center rounded-xl bg-blue-tint text-blue"
      >
        {icon}
      </span>
      <strong className="text-19 text-ink">{title}</strong>
      {children != null && <span className="max-w-[300px] text-muted">{children}</span>}
      {actions != null && <div className="flex flex-wrap justify-center gap-1.5">{actions}</div>}
    </div>
  );
}

/** Grey bars while something loads. Announced once as loading. */
export function Skeleton({
  lines = 3,
  className,
  label = 'Loading',
}: {
  lines?: number;
  className?: string;
  label?: string;
}) {
  const widths = ['w-[80%]', 'w-[65%]', 'w-[72%]', 'w-[55%]', 'w-[68%]'];
  return (
    <div role="status" aria-label={label} className={cn('flex flex-col gap-2', className)}>
      {Array.from({ length: lines }, (_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: static placeholder bars
        <span key={i} className={cn('h-3 rounded-[3px] bg-sunk', widths[i % widths.length])} />
      ))}
    </div>
  );
}

/** A loading page body: header bar and a few blocks. */
export function PageSkeleton() {
  return (
    <div role="status" aria-label="Loading" className="flex flex-col gap-4 p-7">
      <span className="h-4 w-48 rounded-[3px] bg-sunk" />
      <span className="h-7 w-72 rounded-[3px] bg-sunk" />
      <span className="mt-4 h-24 w-full rounded-lg bg-sunk" />
      <span className="h-24 w-full rounded-lg bg-sunk" />
    </div>
  );
}

/** Replaces the message box on a filed year: says what still works. */
export function LockedNotice({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center gap-3 rounded-xl bg-sunk px-4 py-3.5 text-text">
      <LockIcon size={20} />
      <span>{children}</span>
    </div>
  );
}

/** What went wrong and what to do about it. Never apologizes, never vague. */
export function ErrorState({
  title,
  children,
  action,
}: {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div
      role="alert"
      className="flex flex-col items-start gap-2.5 rounded-[10px] border border-line bg-surface p-[18px]"
    >
      <strong className="text-17 text-ink">{title}</strong>
      {children != null && <span className="text-14">{children}</span>}
      {action}
    </div>
  );
}
